import { useState, useEffect, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import chatwidgetService from "../../services/chatwidget.service";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const MEETING_DURATIONS = [
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "45 minutes", value: 45 },
  { label: "60 minutes", value: 60 },
];

const GoogleCalendarDialogPublic = ({ open, onClose, webDetail }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const [eventDialog, setEventDialog] = useState({
    open: false,
    isEditing: false,
  });

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    start: dayjs(),
    end: dayjs().add(15, "minute"),
    eventOwnerEmail: "",
    duration: 15,
  });

  const userId = webDetail?.websiteDetails?.user?.id;

  /** Load session user info */
  useEffect(() => {
    const session = localStorage.getItem("userSession");
    if (session) {
      try {
        setUserInfo(JSON.parse(session).user);
      } catch (err) {
        console.error("Invalid user session", err);
      }
    }
  }, []);

  /** Load events + availability */
  console.log("webDetail==72===", webDetail);
  const calendarUserId = webDetail?.websiteDetails?.user?.id;
  const loadEvents = useCallback(async () => {
    if (!userInfo || !calendarUserId) return;
    setLoading(true);

    try {
      const [booked, freebusy] = await Promise.all([
        chatwidgetService.listEvents(
          {
            timeMin: dayjs().startOf("month").toISOString(),
            timeMax: dayjs().endOf("month").toISOString(),
          },
          calendarUserId
        ),
        chatwidgetService.findAvailability(
          {
            start: dayjs().startOf("day").toISOString(),
            end: dayjs().add(14, "day").endOf("day").toISOString(),
          },
          calendarUserId
        ),
      ]);

      const currentEmail = userInfo.email?.toLowerCase();

      const bookedEvents =
        booked?.map((evt) => {
          const desc = evt?.description?.toLowerCase() || "";
          const attendees = evt?.attendees?.map((a) => a.email?.toLowerCase());
          const ownerEmail =
            attendees?.[0] ||
            desc.match(/\(([^)]+)\)/)?.[1]?.toLowerCase() ||
            evt?.creator?.email?.toLowerCase() ||
            "";

          const isUserEvent = ownerEmail === currentEmail;

          return {
            id: evt.id,
            title: evt.summary,
            start: dayjs(evt.start.dateTime || evt.start.date).toISOString(),
            end: dayjs(evt.end.dateTime || evt.end.date).toISOString(),
            backgroundColor: isUserEvent ? "#a7c7e7" : "#e0e0e0",
            borderColor: isUserEvent ? "#1976d2" : "#9e9e9e",
            editable: isUserEvent,
            extendedProps: { ownerEmail },
          };
        }) || [];

      const unavailableSlots =
        freebusy?.map((b) => ({
          title: "Unavailable",
          start: dayjs(b.start).toISOString(),
          end: dayjs(b.end).toISOString(),
          display: "background",
          backgroundColor: "#f5f5f5",
        })) || [];

      setEvents([...bookedEvents, ...unavailableSlots]);
    } catch (err) {
      console.error("Failed to load calendar data", err);
    } finally {
      setLoading(false);
    }
  }, [userInfo, calendarUserId]);

  useEffect(() => {
    if (open && userInfo) loadEvents();
  }, [open, userInfo, loadEvents]);

  /** Checks if user already has a meeting */
  const userAlreadyHasMeeting = () =>
    events.some(
      (evt) => evt.extendedProps?.ownerEmail === userInfo?.email?.toLowerCase()
    );

  /** Check slot availability */
  const isSlotUnavailable = (start, end) =>
    events.some(
      (evt) =>
        evt.display === "background" &&
        ((start.isSameOrAfter(evt.start) && start.isBefore(evt.end)) ||
          (end.isAfter(evt.start) && end.isSameOrBefore(evt.end)))
    );

  /** When empty slot clicked */
  const handleDateClick = (info) => {
    if (userAlreadyHasMeeting()) {
      alert("You already have a meeting. You can only book ONE.");
      return;
    }

    const start = dayjs(info.date);
    const end = start.add(formData.duration, "minute");

    if (isSlotUnavailable(start, end)) {
      alert("This slot is unavailable. Choose another.");
      return;
    }

    setFormData({
      id: null,
      title: "",
      start,
      end,
      duration: formData.duration,
      eventOwnerEmail: userInfo?.email || "",
    });

    setEventDialog({ open: true, isEditing: false });
  };

  /** When event clicked */
  const handleEventClick = (info) => {
    const { event } = info;

    const ownerEmail = event.extendedProps?.ownerEmail?.toLowerCase();
    const currentEmail = userInfo?.email?.toLowerCase();

    if (event.display === "background" || ownerEmail !== currentEmail) return;

    const duration = dayjs(event.end).diff(dayjs(event.start), "minute");

    setFormData({
      id: event.id,
      title: event.title,
      start: dayjs(event.start),
      end: dayjs(event.end),
      duration,
      eventOwnerEmail: ownerEmail,
    });

    setEventDialog({ open: true, isEditing: true });
  };

  /** Auto update end based on duration */
  const updateEndByDuration = (start, duration) => {
    return start.add(duration, "minute");
  };

  /** Save / Update meeting */
  const handleSave = async () => {
    if (!formData.title.trim()) return;
    if (saving) return; // ⛔ Prevent double-click

    setSaving(true);

    const payload = {
      userId,
      summary: formData.title,
      description: `Booked by ${userInfo?.name || "Guest"} (${
        userInfo?.email
      })`,
      start: {
        dateTime: formData.start.toISOString(),
        timeZone: "Asia/Kolkata",
      },
      end: {
        dateTime: formData.end.toISOString(),
        timeZone: "Asia/Kolkata",
      },
      attendeeEmail: userInfo?.email,
      attendeeName: userInfo?.name,
    };

    try {
      if (eventDialog.isEditing)
        await chatwidgetService.updateEvent(
          {
            eventId: formData.id,
            ...payload,
          },
          webDetail?.websiteDetails?.user?.id
        );
      else
        await chatwidgetService.createEvent(
          payload,
          webDetail?.websiteDetails?.user?.id
        );

      await loadEvents();
      setEventDialog({ open: false, isEditing: false });
    } catch (err) {
      console.error("Failed to save event", err);
    } finally {
      setSaving(false);
    }
  };

  /** Delete meeting */
  const handleDelete = async () => {
    try {
      await chatwidgetService.deleteEvent(
        { eventId: formData.id },
        webDetail?.websiteDetails?.user?.id
      );
      await loadEvents();
      setEventDialog({ open: false, isEditing: false });
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const closeEventDialog = () =>
    setEventDialog({ open: false, isEditing: false });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ position: "relative" }}>
          📅 Book or Manage Google Meetings
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="70vh"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: "70vh" }}>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                selectable
                events={events}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventDidMount={(info) => {
                  const ownerEmail =
                    info.event.extendedProps?.ownerEmail?.toLowerCase();
                  const currentEmail = userInfo?.email?.toLowerCase();
                  if (ownerEmail && ownerEmail !== currentEmail) {
                    info.el.style.opacity = "0.6";
                    info.el.style.cursor = "not-allowed";
                  }
                }}
                height="100%"
                slotDuration="00:30:00"
                nowIndicator
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>

        {/* EVENT DIALOG */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Dialog open={eventDialog.open} onClose={closeEventDialog}>
            <DialogTitle>
              {eventDialog.isEditing ? "Edit Meeting" : "Book Meeting"}
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Meeting Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                margin="dense"
              />

              {/* Meeting Duration */}
              <TextField
                fullWidth
                select
                label="Duration"
                value={formData.duration}
                onChange={(e) => {
                  const duration = Number(e.target.value);
                  setFormData((prev) => ({
                    ...prev,
                    duration,
                    end: updateEndByDuration(prev.start, duration),
                  }));
                }}
                margin="dense"
              >
                {MEETING_DURATIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Start Picker */}
              <DateTimePicker
                label="Start Time"
                value={formData.start}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    start: val,
                    end: updateEndByDuration(val, prev.duration),
                  }))
                }
                slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
              />
            </DialogContent>

            <DialogActions>
              {eventDialog.isEditing && (
                <Button color="error" onClick={handleDelete}>
                  Delete
                </Button>
              )}
              <Button onClick={closeEventDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!formData.title.trim() || saving}
              >
                {saving
                  ? "Saving..."
                  : eventDialog.isEditing
                  ? "Update"
                  : "Book"}
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>
      </Dialog>
    </>
  );
};

export default GoogleCalendarDialogPublic;
